import type { FieldWizardDefinition } from '@/routers/inventorization/components/GenericFieldEditor';
import { VALUE_SCAN_PURPOSES, type ValueScanPurpose } from '@/shared/camera/value-scanner';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import type { OcrCandidateField } from '@/shared/ocr/ocr-candidates';
import { OCR_CANDIDATE_FIELDS } from '@/shared/ocr/ocr-candidates';

export type SaveResult = Promise<unknown> | unknown;

export function textField(
  currentValue: string,
  description: string,
  placeholder: string,
  save: (value: string) => SaveResult,
  ocrField?: OcrCandidateField,
): FieldWizardDefinition {
  return {
    currentValue,
    description,
    ocrField,
    photoOcrPurpose: getPhotoOcrPurposeForOcrField(ocrField),
    placeholder,
    save,
    type: 'text',
  };
}

export function nativeSelectField(
  currentValue: string,
  description: string,
  placeholder: string,
  options: Array<{ label: string; value: string }>,
  save: (value: string) => SaveResult,
): FieldWizardDefinition {
  return {
    currentValue,
    description,
    options,
    placeholder,
    save,
    type: 'native-select',
  };
}

export function catalogField(
  currentValue: string,
  description: string,
  placeholder: string,
  options: Array<{ label: string; value: string }>,
  save: (value: string) => SaveResult,
): FieldWizardDefinition {
  return {
    currentValue,
    description,
    options: options.toSorted((left, right) => left.label.localeCompare(right.label)),
    placeholder,
    save,
    type: 'catalog',
  };
}

export function optionField(
  currentValue: string,
  description: string,
  placeholder: string,
  options: Array<{ label: string; value: string }>,
  save: (value: string) => SaveResult,
  ocrField?: OcrCandidateField,
): FieldWizardDefinition {
  return {
    currentValue,
    description,
    ocrField,
    options: options.toSorted(compareOptionLabels),
    photoOcrPurpose: getPhotoOcrPurposeForOcrField(ocrField),
    placeholder,
    save,
    type: 'options',
  };
}

export function rssiField(
  currentValue: string,
  description: string,
  save: (value: string) => SaveResult,
): FieldWizardDefinition {
  const negativeHint = translateInventoryText(
    'app.inventory.fieldWizard.rssi.negativeHint',
    'Enter a negative dBm value.',
  );

  return {
    ...textField(normalizeRssiInputValue(currentValue), `${description} ${negativeHint}`, '-111', value =>
      save(normalizeRssiInputValue(value)),
    ),
    type: 'number',
  };
}

function compareOptionLabels(left: { label: string }, right: { label: string }) {
  const leftNumber = readLeadingNumber(left.label);
  const rightNumber = readLeadingNumber(right.label);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return left.label.localeCompare(right.label);
}

function readLeadingNumber(value: string) {
  const match = value.match(/^-?\d+(?:[,.]\d+)?/);

  return match ? Number(match[0].replace(',', '.')) : Number.NaN;
}

function normalizeRssiInputValue(value: string) {
  const text = value.trim();

  if (!text) {
    return '';
  }

  const numberValue = Number(text.replace(',', '.'));

  if (Number.isFinite(numberValue)) {
    return String(numberValue === 0 ? 0 : -Math.abs(numberValue));
  }

  return text.startsWith('-') ? text : `-${text}`;
}

const photoOcrPurposeByCandidateField: Partial<Record<OcrCandidateField, ValueScanPurpose>> = {
  [OCR_CANDIDATE_FIELDS.registerCounter]: VALUE_SCAN_PURPOSES.registerCounter,
  [OCR_CANDIDATE_FIELDS.registerObis]: VALUE_SCAN_PURPOSES.registerObis,
  [OCR_CANDIDATE_FIELDS.registerUnit]: VALUE_SCAN_PURPOSES.registerUnit,
  [OCR_CANDIDATE_FIELDS.serial]: VALUE_SCAN_PURPOSES.serial,
};

function getPhotoOcrPurposeForOcrField(ocrField: OcrCandidateField | undefined): ValueScanPurpose | undefined {
  return ocrField ? photoOcrPurposeByCandidateField[ocrField] : undefined;
}
